import { memo, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useChatStreamValue } from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";
import { useResponsive } from "src/reusable/hooks/useResponsive";
import { useUIStore } from "../../stores/UIStore";
import { useChatStore } from "../../stores/ChatStore";
import { ChatMessageWithParts } from "../../types";
import UnifiedSidebar from "src/reusable/Sidebar/UnifiedSidebar";
import { useAtomAssistSidebar } from "../sidebar/useAtomAssistSidebar";
import SidebarToggleButton from "src/reusable/Sidebar/SidebarToggleButton";
import { useDialogueProvenance } from "../../services/ProvenanceService";
import { countProvenanceTiers } from "../sources/provenanceCounts";
import {
  overlayLiveProvenance,
  useLiveWebExternals,
} from "../sources/useLiveWebExternals";

const EMPTY_ARTIFACTS = {};

export const AASidebar = memo(
  ({
    responseSources,
    lastAssistantMessage,
    dialogueId: dialogueIdProp,
  }: {
    responseSources: any[];
    lastAssistantMessage?: ChatMessageWithParts | null;
    dialogueId?: number;
  }) => {
    const { isMobile } = useResponsive();

    // Get artifacts and dialogue ID. Prop wins over URL param so this works
    // when AtomAssistPage is embedded inside ChatPanePanel (no :dialogueId
    // URL segment); otherwise fall back to the route param for the
    // standalone /atom-assist/chat/:dialogueId entry point.
    const { dialogueId: dialogueIdParam } = useParams<{ dialogueId: string }>();
    const dialogueId =
      dialogueIdProp !== undefined ? dialogueIdProp : Number(dialogueIdParam);
    // Read the host-owned store (every AASidebar host provides one).
    const artifacts = useChatStreamValue((s) => s.artifacts, EMPTY_ARTIFACTS);

    // Session-accumulated cited count drives the Sources badge (#20423 Story 3).
    const { data: dialogueProvenance } = useDialogueProvenance(
      Number.isFinite(dialogueId) ? dialogueId : null,
    );
    // Count via the SHARED tier helper so the badge matches the panel and the
    // per-response summary — a web-only answer cites externals with no chunk
    // rows, so counting assistant_response_sources alone reported 0/1 instead
    // of the real cited total (#20423 A2). Live web-search sources overlay the
    // payload the same way the panel does, so the badge counts a still-
    // streaming web answer's citations without waiting on provenance.
    const live = useLiveWebExternals(
      Number.isFinite(dialogueId) ? dialogueId : null,
    );
    const citedCount = useMemo(() => {
      if (
        !dialogueProvenance &&
        live.externals.size === 0 &&
        live.citedChunkIds.length === 0
      ) {
        return undefined;
      }
      return countProvenanceTiers(
        overlayLiveProvenance(dialogueProvenance, live, responseSources),
      ).citedCount;
    }, [dialogueProvenance, live, responseSources]);

    // Get UI store state
    const { rightSidebar, setRightSidebar } = useUIStore();
    const routing = useChatStore((state) => state.routing);
    const showSideBar = rightSidebar.isOpen;

    // Local state for source view mode and detail views
    const [viewMode, setViewMode] = useState<"dataChunk" | "cardList">(
      "dataChunk",
    );
    const [detailSourceViewSdrId, setDetailSourceViewSdrId] = useState<
      number | null
    >(null);
    const [detailSourceViewSourceId, setDetailSourceViewSourceId] = useState<
      number | null
    >(null);

    // Memoize total sidebar items for mobile badge
    const totalSidebarItems = useMemo(() => {
      return responseSources.length + Object.keys(artifacts ?? {}).length;
    }, [responseSources.length, artifacts]);

    // Get sidebar configuration
    const sidebarConfig = useAtomAssistSidebar();

    // Memoize context to prevent unnecessary re-renders of UnifiedSidebar (which is wrapped in memo)
    // State setters from useState are stable and don't need to be in deps
    const sidebarContext = useMemo(
      () => ({
        responseSources,
        artifacts,
        routing,
        viewMode,
        setViewMode,
        detailSourceViewSdrId,
        detailSourceViewSourceId,
        setDetailSourceViewSdrId,
        setDetailSourceViewSourceId,
        // Provenance funnel needs the dialogue + message to fetch the
        // Searched/Read/Cited tiers for the latest assistant response.
        dialogueId: Number.isFinite(dialogueId) ? dialogueId : null,
        messageId: lastAssistantMessage?.id ?? null,
        citedCount,
      }),
      [
        responseSources,
        artifacts,
        routing,
        viewMode,
        detailSourceViewSdrId,
        detailSourceViewSourceId,
        dialogueId,
        lastAssistantMessage?.id,
        citedCount,
      ],
    );

    const handleMobileToggle = useCallback(() => {
      setRightSidebar({
        isOpen: !showSideBar,
        ...(!showSideBar &&
          rightSidebar.selectedItem === null && { selectedItem: "sources" }),
      });
    }, [showSideBar, rightSidebar.selectedItem, setRightSidebar]);

    return (
      <>
        <UnifiedSidebar
          config={sidebarConfig}
          context={sidebarContext}
          open={rightSidebar.isOpen}
          onOpenChange={(open) => {
            // NOTE: we intentionally do NOT fire `verification_opened`
            // here — VerificationPane fires it on mount, which is the
            // single source of truth for "user actually saw the pane".
            // Firing from both spots double-counts (and also counted
            // opens where the selected tab wasn't verification at all).
            setRightSidebar({ isOpen: open });
          }}
          selectedTab={rightSidebar.selectedItem}
          onTabChange={(tab) =>
            setRightSidebar({
              selectedItem: tab,
              // Reset artifact-specific state when switching tabs
              // This ensures showing artifact list instead of stale detail view
              isArtifactItemOpen: false,
              currentArtifactId: null,
            })
          }
          width={rightSidebar.width}
          onWidthChange={(width) => setRightSidebar({ width })}
        />

        {isMobile && (
          <SidebarToggleButton
            open={showSideBar}
            onClick={handleMobileToggle}
            badgeContent={totalSidebarItems}
            tooltipTitle={
              showSideBar ? "Close sidebar" : `View ${totalSidebarItems} items`
            }
            show={totalSidebarItems > 0}
          />
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.responseSources === nextProps.responseSources &&
      prevProps.lastAssistantMessage?.id === nextProps.lastAssistantMessage?.id
    );
  },
);

export default AASidebar;
